import type { Parametros } from '@favo/core';

import { Celula } from '@/componentes/celula';
import { formatarMoeda, formatarPercentualPontos, SEM_VALOR } from '@/design/formato';

import { formatarVolume } from './grandezas';

/**
 * A ficha de parâmetros, no fim do documento porque é base de consulta e não
 * conclusão. Campo areia: ela CONSTATA, não opina. O mel dizia "aqui é
 * custo", que é só parte do que a lista carrega.
 */
export function Premissas({ p }: { p: Parametros }) {
  const base = (b: 'total' | 'servico') => (b === 'servico' ? 'serviço' : 'venda total');

  const origem =
    p.ticketOrigem === 'projeto'
      ? 'valor de venda do projeto'
      : p.ticketOrigem === 'sazonalidade'
        ? 'ticket médio da sazonalidade'
        : 'sem preço informado';

  // `largo`: valor que é uma lista, e lista numa coluna de 232px vira escada.
  const itens: { rotulo: string; valor: string; mono?: boolean; largo?: boolean }[] = [
    {
      rotulo: `Preço de venda (${origem})`,
      valor: p.ticket > 0 ? formatarMoeda(p.ticket) : SEM_VALOR,
      mono: true,
    },
    { rotulo: 'Custo direto total', valor: formatarMoeda(p.cdt), mono: true },
    { rotulo: 'Custo do kit', valor: formatarMoeda(p.kit), mono: true },
    {
      rotulo: 'Comissão de vendas',
      valor: `${formatarPercentualPontos(p.comissao)} sobre ${base(p.comissaoBase)}`,
    },
    {
      rotulo: 'Carga tributária',
      valor: `${formatarPercentualPontos(p.tributo)} sobre ${base(p.tributoBase)}`,
    },
    {
      rotulo: 'Outras despesas variáveis',
      valor: p.outras > 0 ? formatarPercentualPontos(p.outras) : 'Não aplicável',
    },
    { rotulo: 'Despesa fixa por mês', valor: formatarMoeda(p.despFixa), mono: true },
    {
      rotulo: 'Meta de margem líquida',
      valor: `${formatarPercentualPontos(p.lucroAlvo)}${p.lucroAlvoDefinido ? '' : ' (padrão do sistema)'}`,
    },
    { rotulo: 'Volume anual', valor: p.V12 > 0 ? `${formatarVolume(p.V12)} projetos` : SEM_VALOR },
  ];

  if (p.extras.length > 0) {
    itens.push({
      rotulo: 'Custos extras',
      valor: p.extras.map((e) => `${e.label}: ${formatarMoeda(e.valor)}`).join(' · '),
      largo: true,
    });
  }
  if (p.despesasExtras.length > 0) {
    itens.push({
      rotulo: 'Despesas variáveis extras',
      valor: p.despesasExtras
        .map((e) => `${e.nome}: ${formatarPercentualPontos(e.valor)} sobre ${base(e.base)}`)
        .join(' · '),
      largo: true,
    });
  }

  return (
    <Celula como="section" campo="areia" className="secao anexo">
      <span className="marca-anexo">Anexo</span>
      <h2 className="titulo-bloco titulo-anexo">Premissas usadas</h2>
      <dl className="premissas">
        {itens.map((item) => (
          <div key={item.rotulo} {...(item.largo ? { 'data-largo': '' } : {})}>
            <dt>{item.rotulo}</dt>
            <dd className={item.mono ? 'cifra' : undefined}>{item.valor}</dd>
          </div>
        ))}
      </dl>
      <p className="apoio-nota mt-5">
        Custo não configurado vale zero, nunca um valor estimado. A despesa fixa é rateada por
        volume: despesa mensal vezes 12, dividida pelos projetos do ano.
      </p>
    </Celula>
  );
}
